import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useField, useFormikContext } from 'formik'
import { useInfiniteQuery } from 'react-query'

import { useClient } from '@hooks/useClient'
import { useDebounce } from '@hooks/useDebounce'
import { EbookOptionType } from '@models/dashboard/SearchEbookResponse'
import { useOutside } from '@hooks/useOutside'
import { WithSearchEbookFieldProps, SearchEbookFieldProps } from './interface'

const withSearchEbookField = (Component: React.FC<SearchEbookFieldProps>) => {
  function WithSearchEbookField({
    name,
    text,
    setText,
    resetHandler,
    ...props
  }: WithSearchEbookFieldProps) {
    const client = useClient()
    const searchRef = useRef<HTMLDivElement>(null)
    const [isShowSearchLog, setIsShowSearchLog] = useState<boolean>(false)
    const [textSearch, setTextSearch] = useState('')
    const { submitForm } = useFormikContext()
    const [field, , { setValue }] = useField(name)
    const { debounce } = useDebounce()

    const { fetchNextPage, data, isLoading, isFetchingNextPage } =
      useInfiniteQuery(
        ['search-user', textSearch],
        ({ pageParam = 1 }) =>
          client?.dashboardClient.searchEbook(textSearch, pageParam),
        {
          getNextPageParam: lastPage => {
            if (lastPage && lastPage.data.length) return lastPage.page + 1

            return undefined
          },
        }
      )

    function handleFetchNextPage() {
      if (!isFetchingNextPage) fetchNextPage()
    }

    function showSearchLog() {
      setIsShowSearchLog(true)
    }

    function hideSearchLog() {
      setIsShowSearchLog(false)
    }

    function handleTextChange(event: React.ChangeEvent<HTMLInputElement>) {
      setText(event.target.value)
    }

    function handleSelectEbook(ebook: EbookOptionType) {
      setValue(ebook.id)
      setText(ebook.title)
      hideSearchLog()
      submitForm()
    }

    function removeTextSearch() {
      if (resetHandler) {
        resetHandler()
      }
      setValue(undefined)
      setText('')
    }

    const ebooks = useMemo(
      () => data?.pages.flatMap(page => page!.data) || [],
      [data]
    )

    useOutside(searchRef, () => {
      const prevText =
        ebooks.find(ebook => ebook.id === field.value)?.title || ''
      hideSearchLog()
      setText(prevText)
    })

    useEffect(() => {
      debounce(() => {
        setTextSearch(text)
      }, 400)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text])

    const componentsProps = {
      ...props,
      text,
      isShowSearchLog,
      value: field.value || '',
      ebooks,
      showSearchLog,
      handleSelectEbook,
      handleTextChange,
      searchRef,
      handleFetchNextPage,
      removeTextSearch,
    }
    return <Component {...componentsProps} />
  }

  return WithSearchEbookField
}

export { withSearchEbookField }
